import type { ReactNode } from 'react'
import BottomSheetContent from '../../../../../../../components/BottomSheetComponents/BottomSheetContent'
import RenderHTML from '../../../../../../../components/ui-components/RenderHTML'
import { lightImpactVibrate, openInBrowser, postCTA } from '../../../../../../../utils/appBridge'
import {
  LumpsumImportantNoteRedirectionType,
  type LumpsumImportantNote
} from '../../api/recommendation.types'

const MANUAL_BOOK_ICON = `<svg width="48" height="48" viewBox="0 0 38.4286 45.0519" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M14.2468 12.7688C14.2468 11.7863 14.5381 10.8259 15.0839 10.009C15.6298 9.19206 16.4056 8.55535 17.3133 8.17938C18.221 7.8034 19.2198 7.70504 20.1834 7.89669C21.147 8.08837 22.0321 8.56148 22.7269 9.2562C23.4216 9.95093 23.8947 10.836 24.0864 11.7997C24.2781 12.7633 24.1797 13.7621 23.8037 14.6698C23.4277 15.5775 22.791 16.3533 21.9741 16.8992C21.1572 17.445 20.1968 17.7363 19.2143 17.7363V19.3922" stroke="#232323" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M19.2143 26.2225C18.757 26.2225 18.3864 25.8518 18.3864 25.3946C18.3864 24.9373 18.757 24.5667 19.2143 24.5667" stroke="#232323" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M19.2143 26.2225C19.6715 26.2225 20.0422 25.8518 20.0422 25.3946C20.0422 24.9373 19.6715 24.5667 19.2143 24.5667" stroke="#232323" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M37.4286 44.052H5.96753C4.65008 44.052 3.38654 43.5287 2.45496 42.5971C1.52338 41.6655 1 40.4018 1 39.0844C1 37.767 1.52338 36.5033 2.45496 35.5717C3.38654 34.6401 4.65008 34.1169 5.96753 34.1169H34.1169C34.9951 34.1169 35.8376 33.7678 36.4586 33.1469C37.0795 32.526 37.4286 31.6835 37.4286 30.8052V4.31169C37.4286 3.43336 37.0795 2.59103 36.4586 1.96997C35.8376 1.34891 34.9951 1 34.1169 1H5.96753C4.67269 0.99954 3.42886 1.50468 2.50099 2.40782C1.57309 3.31096 1.03454 4.54069 1 5.83506V38.952" stroke="#232323" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M34.1169 34.1169V44.0519" stroke="#232323" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`

type PlanImportantNoteDynamicInfoBottomSheetProps = {
  header: string
  body?: string | null
  onDismiss: () => void
}

export function handleDynamicImportantNoteRedirection(
  importantNote: LumpsumImportantNote | null | undefined,
  openNestedBottomSheet: (content: ReactNode) => void,
  closeNestedBottomSheet: () => void
) {
  const redirection = importantNote?.hyperlink?.redirection
  const redirectionType = redirection?.type
  const redirectionCta = redirection?.redirectionCta

  if (redirectionType === LumpsumImportantNoteRedirectionType.BOTTOMSHEET) {
    lightImpactVibrate()
    openNestedBottomSheet(
      <PlanImportantNoteDynamicInfoBottomSheet
        header={redirection?.header?.trim() ?? 'Important Note'}
        body={redirection?.body}
        onDismiss={closeNestedBottomSheet}
      />
    )
    return
  }

  if (redirectionType === LumpsumImportantNoteRedirectionType.CTA_REDIRECTION) {
    if (redirectionCta == null) {
      return
    }
    lightImpactVibrate()
    postCTA(JSON.stringify(redirectionCta))
    return
  }

  if (redirectionType === LumpsumImportantNoteRedirectionType.WEB_REDIRECTION) {
    const redirectionUrl =
      typeof redirectionCta?.url === 'string' ? redirectionCta.url.trim() : ''
    if (redirectionUrl.length === 0) {
      return
    }
    lightImpactVibrate()
    openInBrowser(redirectionUrl)
  }
}

export function buildFactBasedImportantNotePoints(
  importantNotes: LumpsumImportantNote[] | null | undefined,
  openNestedBottomSheet: (content: ReactNode) => void,
  closeNestedBottomSheet: () => void
): ReactNode[] {
  const points: ReactNode[] = []
  const linkClassName =
    'w-fit cursor-pointer text-[13px] font-[400] leading-[140%] text-[#1470CC] underline underline-offset-2'
  const textClassName = 'text-[13px] font-[400] leading-[150%] text-[#3D3D3D]'

  for (const importantNote of importantNotes ?? []) {
    const text = importantNote?.text?.trim() ?? ''
    if (text.length === 0) {
      continue
    }

    const link = importantNote?.hyperlink?.hyperlinkText?.trim() ?? ''
    if (link.length === 0) {
      points.push(<div className={textClassName}>{text}</div>)
      continue
    }

    const onLinkClick = () =>
      handleDynamicImportantNoteRedirection(
        importantNote,
        openNestedBottomSheet,
        closeNestedBottomSheet
      )

    const linkStartIndex = text.indexOf(link)
    if (linkStartIndex < 0) {
      points.push(
        <div>
          <div className={textClassName}>{text}</div>
          <div className={linkClassName} onClick={onLinkClick}>
            {link}
          </div>
        </div>
      )
      continue
    }

    points.push(
      <div className={textClassName}>
        {text.slice(0, linkStartIndex)}
        <span
          className="cursor-pointer text-[#1470CC] underline underline-offset-2"
          onClick={onLinkClick}
        >
          {link}
        </span>
        {text.slice(linkStartIndex + link.length)}
      </div>
    )
  }

  return points
}

export function PlanImportantNoteDynamicInfoBottomSheet({
  header,
  body,
  onDismiss
}: PlanImportantNoteDynamicInfoBottomSheetProps) {
  const bodyHtml = body?.trim() ?? ''

  return (
    <BottomSheetContent
      heading={header}
      logoSvg={<RenderHTML innerHTMLText={MANUAL_BOOK_ICON} className="size-12 shrink-0" />}
      onDismiss={onDismiss}
      bottomSheetHeadClassName="!pt-6"
      bottomSheetHeadingClassName="!text-[22px] !text-[#232323] !leading-[130%] !font-[400] !mb-0"
      bottomSheetBodyClassName="!pt-3 !pb-0"
    >
      {bodyHtml.length > 0 ? (
        <RenderHTML
          className="text-[14px] font-[400] leading-[150%] text-[#6A6E6E]"
          innerHTMLText={bodyHtml}
        />
      ) : null}
    </BottomSheetContent>
  )
}
